/**
 * Turns a page of `/chat/list-media` (or `/chat/list-files`) rows into
 * displayable URLs through `media-api`'s private `/object/get-batch`, so
 * neither the grid, the viewer, nor the files list joins two services
 * inline.
 */
import { useObjectGetBatch } from "@sportsmet/media-sdk/private";

export type typeResolvedMedia = {
  thumbnailUrl: string;
  largeUrl: string;
  originalUrl: string;
} | null;

/**
 * RF-4: `services/media-api/schemas/private.openapi.json` caps
 * `mediaIds` at 200 items. Ids past this cap map to `null` like any other
 * unresolved id; the real fix is the caller keeping its own list page at or
 * under this size (`{ chatId, limit: 200 }`).
 */
export const MEDIA_BATCH_MAX = 200;

export function useChatMediaUrls(
  mediaObjectIds: readonly string[],
): Map<string, typeResolvedMedia> {
  // No early `return` before the hook call: the hook count must stay stable
  // across renders, including when `mediaObjectIds` is empty.
  const boundedIds = [...mediaObjectIds].slice(0, MEDIA_BATCH_MAX);
  const objectGetBatch = useObjectGetBatch({ mediaIds: boundedIds });

  const items = objectGetBatch.data?.data?.items ?? [];
  const byId = new Map<string, (typeof items)[number]>();
  for (const item of items) {
    byId.set(item.mediaId, item);
  }

  const resolved = new Map<string, typeResolvedMedia>();
  for (const id of mediaObjectIds) {
    const item = byId.get(id);
    const variants = item?.variants ?? null;
    resolved.set(
      id,
      variants
        ? {
            thumbnailUrl: variants.thumbnail.url,
            largeUrl: variants.large.url,
            originalUrl: variants.original.url,
          }
        : null,
    );
  }
  return resolved;
}
